import { Loading } from '@tencent/odc-iconpack';
import { Tooltip } from '@tencent/one-design-next';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import './index.scss';

export type ButtonType = 'primary' | 'default' | 'text' | 'link' | 'icon' | 'error';
export interface IButton {
  className?: string;
  type?: ButtonType;
  loading?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseUp?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseOver?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseOut?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children?: React.ReactNode;
  htmlref?: React.Ref<HTMLButtonElement>;
  disabledTip?: string;
}

interface Ripple {
  id: number;
}

function Button(props: IButton, btnRef: React.ForwardedRef<HTMLButtonElement>) {
  const {
    className,
    type = 'default',
    children,
    loading = false,
    disabled = false,
    disabledTip,
    size,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
    onMouseOver,
    onMouseOut,
    onClick,
    style,
    htmlref,
  } = props;
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const [ripples, setRipples] = useState<Ripple[]>([]);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || type === 'link') return;

    const id = Date.now();

    setRipples((prev) => [...prev, { id }]);

    // 动画结束后移除涟漪
    const timeoutId = setTimeout(() => {
      setRipples((prev) => prev.filter((ripple) => ripple.id !== id));
      timers.current.delete(timeoutId);
    }, 600);

    timers.current.add(timeoutId);

    onMouseDown?.(e);
  }, [disabled, onMouseDown]);

  useEffect(() => {
    return () => {
      timers.current.forEach((timeoutId) => clearTimeout(timeoutId));
      timers.current.clear();
    };
  }, []);
  const btn = (
    <button
      ref={htmlref || btnRef}
      className={classNames('odc-button', className, {
        'odc-button-primary': type === 'primary',
        'odc-button-default': type === 'default',
        'odc-button-text': type === 'text',
        'odc-button-link': type === 'link',
        'odc-button-icon': type === 'icon',
        'odc-button-error': type === 'error',
        'odc-button-loading': loading,
        'odc-button-disabled': disabled,
        'odc-button-xs': size === 'xs',
        'odc-button-sm': size === 'sm',
        'odc-button-md': size === 'md',
      })}
      style={style}
      type="button"
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      role="button"
      aria-pressed="false"
      aria-hidden="false"
      aria-label="button"
      disabled={disabled}
      onMouseDown={handleMouseDown}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onClick={onClick}
    >
      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="odc-button-ripple"
        />
      ))}
      <div className="odc-button-content">{children}</div>
      {loading && (
        <div className="odc-button-loading">
          <Loading
            className="animate-spin"
            style={{
              '--odc-icon-loading-bg':
                'conic-gradient(from 90deg, rgba(255, 255, 255, 0.05) 0deg, rgba(255, 255, 255, 1) 360deg)',
            }}
          />
        </div>
      )}
    </button>
  );

  if (disabled && disabledTip) {
    return (
      <Tooltip popup={disabledTip}
        placement="bottom"
        trigger="hover"
        arrowed={false}
      >
        {btn}
      </Tooltip>
    );
  }
  return btn;
}

export default React.forwardRef(Button);